/** Debug logging for BLE / ESC/POS printing (dev or VITE_PRINTER_DEBUG=1). */

const TAG = '[ZeettuPrinter]';
const MAX_LOG_ENTRIES = 100;

type PrinterLogLevel = 'info' | 'warn';

type PrinterLogEntry = {
  at: number;
  level: PrinterLogLevel;
  message: string;
  detail?: string;
};

const logBuffer: PrinterLogEntry[] = [];

function envTruthy(raw: string | undefined): boolean {
  const s = String(raw ?? '').trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes';
}

function serializeLogArg(arg: unknown): string {
  if (arg === undefined) return '';
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return arg.message;
  if (arg instanceof Uint8Array) {
    return previewEscPosBytes(arg);
  }
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function pushPrinterLog(level: PrinterLogLevel, args: unknown[]): void {
  const message = args.map(serializeLogArg).filter(Boolean).join(' ');
  const entry: PrinterLogEntry = { at: Date.now(), level, message };
  if (args.length > 1) {
    const detailParts = args.slice(1).map(serializeLogArg).filter(Boolean);
    if (detailParts.length) entry.detail = detailParts.join(' ');
  }
  logBuffer.push(entry);
  if (logBuffer.length > MAX_LOG_ENTRIES) {
    logBuffer.splice(0, logBuffer.length - MAX_LOG_ENTRIES);
  }
}

/** Recent in-app printer log lines (newest last). */
export function getPrinterLogEntries(): readonly PrinterLogEntry[] {
  return logBuffer;
}

export function clearPrinterLogBuffer(): void {
  logBuffer.length = 0;
}

export function formatPrinterLogText(entries: readonly PrinterLogEntry[] = logBuffer): string {
  if (entries.length === 0) {
    return '(no printer log entries yet — connect, print a test message, or enable VITE_PRINTER_DEBUG=1)';
  }
  return entries
    .map((e) => {
      const ts = new Date(e.at).toISOString();
      const head = `[${ts}] ${e.level.toUpperCase()} ${e.message}`;
      return e.detail ? `${head}\n  ${e.detail}` : head;
    })
    .join('\n');
}

/** Full byte dump as space-separated hex (16 bytes per line). */
export function formatBytesHex(bytes: Uint8Array, bytesPerLine = 16): string {
  if (bytes.length === 0) return '(empty)';
  const lines: string[] = [];
  for (let i = 0; i < bytes.length; i += bytesPerLine) {
    const slice = bytes.subarray(i, i + bytesPerLine);
    const hex = Array.from(slice)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(' ');
    const offset = i.toString(16).padStart(4, '0');
    lines.push(`${offset}  ${hex}`);
  }
  return lines.join('\n');
}

export function isPrinterDebugEnabled(): boolean {
  if (import.meta.env?.DEV) return true;
  return envTruthy(import.meta.env?.VITE_PRINTER_DEBUG);
}

export function printerDebug(...args: unknown[]): void {
  pushPrinterLog('info', args);
  if (!isPrinterDebugEnabled()) return;
  console.info(TAG, ...args);
}

export function printerDebugWarn(...args: unknown[]): void {
  pushPrinterLog('warn', args);
  if (!isPrinterDebugEnabled()) return;
  console.warn(TAG, ...args);
}

/** First/last bytes as hex for transport logs (no full payload dump). */
/** Prefer UTF-8 text preview (TSPL); fall back to hex for binary ESC/POS. */
export function previewPayloadBytes(bytes: Uint8Array, head = 24, tail = 12): string {
  try {
    const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    if (/^[\x20-\x7e\r\n\t]+$/.test(text.slice(0, Math.min(80, text.length)))) {
      const snippet = text.replace(/\r\n/g, '\\n').slice(0, 200);
      return `${bytes.length}B tspl="${snippet}${text.length > 200 ? '…' : ''}"`;
    }
  } catch {
    /* binary */
  }
  return previewEscPosBytes(bytes, head, tail);
}

export function previewEscPosBytes(bytes: Uint8Array, head = 24, tail = 12): string {
  if (bytes.length === 0) return '(empty)';
  const h = Array.from(bytes.subarray(0, Math.min(head, bytes.length)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
  if (bytes.length <= head + tail) return `${bytes.length}B [${h}]`;
  const t = Array.from(bytes.subarray(bytes.length - tail))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
  return `${bytes.length}B [${h} … ${t}]`;
}
